import React, { useState, useEffect, useCallback } from 'react';
import { teamAPI } from '../services/api';
import AppointmentDetail from './AppointmentDetail';

const STATUTS = {
  nouveau:   { label: 'Nouveau',     color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
  confirme:  { label: 'Confirmé',     color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  annule:    { label: 'Annulé',      color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  rencontre: { label: 'Rencontre',    color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
  no_show:   { label: 'No show',     color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  injoignable:{ label: 'Injoignable', color: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  rdv2:      { label: '2ᵉ RDV',       color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  refus:     { label: 'Refusé',      color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('fr-CA', { day: '2-digit', month: 'short', weekday: 'short' }); } catch { return d; }
}
function fmtTime(t) { return t ? String(t).slice(0, 5) : '—'; }

const badge = (s) => {
  const m = STATUTS[s] || STATUTS.nouveau;
  return <span style={{ padding: '2px 8px', borderRadius: 100, fontSize: 11, fontWeight: 800, color: m.color, background: m.bg, border: `1.5px solid ${m.color}` }}>{m.label}</span>;
};

export default function Dashboard({ user, teamData, advisorToken, advisorPassword, showSettings, onSettings, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [statut, setStatut] = useState('');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState('');
  const [err, setErr] = useState('');

  const isAdvisor = user.isAdvisor || user.role === 'advisor';

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (period === 'today') params.period = 'today';
      else if (period === 'week') params.period = 'week';
      else if (period === 'upcoming') params.period = 'upcoming';
      if (statut) params.statut = statut;
      if (q.trim()) params.q = q.trim();

      let data;
      if (isAdvisor && advisorToken) {
        // Read advisor password from localStorage if available
        const storedPw = localStorage.getItem('advisor_password');
        const pw = advisorPassword || storedPw;
        if (pw) {
          data = await teamAPI.getAllAppointments(advisorToken, pw, params);
        } else {
          data = await teamAPI.getMyAppointments(params);
        }
      } else {
        data = await teamAPI.getMyAppointments(params);
      }
      setRows(Array.isArray(data) ? data : []);
      setErr('');
    } catch (e) {
      setRows([]);
      setErr('Impossible de charger les rendez-vous');
    }
    finally { setLoading(false); }
  }, [period, statut, q, isAdvisor, advisorToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const handleRetour = async (r, newStatut) => {
    let followup_at = null;
    let retour = r.retour || '';
    let payload = { statut: newStatut, retour, followup_at };

    if (newStatut === 'injoignable' || newStatut === 'rdv2') {
      const d = window.prompt(
        newStatut === 'injoignable'
          ? "Date du rappel par VOUS (AAAA-MM-JJ HH:MM, heure du Québec) :"
          : "Date du rappel par le CLIENT — 2ᵉ RDV (AAAA-MM-JJ HH:MM, heure du Québec) :", '');
      if (!d) return;
      followup_at = d;
      payload.followup_at = d;
      const note = window.prompt('Note (optionnel) :', retour) ?? retour;
      payload.retour = note;
    } else if (newStatut === 'no_show' || newStatut === 'rencontre' || newStatut === 'refus') {
      const msg = newStatut === 'rencontre' ? 'Résultat de la rencontre (optionnel) :'
        : newStatut === 'no_show' ? 'No show — note (optionnel) :'
        : 'Refus — note (optionnel) :';
      const note = window.prompt(msg, retour);
      if (note === null) return;
      retour = note;
      payload.retour = note;
    }

    try {
      if (isAdvisor && advisorToken && advisorPassword) {
        await teamAPI.advisorRetour(advisorToken, r.id, payload, advisorPassword);
      } else {
        await teamAPI.retour(r.id, payload);
      }
      flash('✓ Enregistré');
      load();
    } catch (e) { alert('Impossible : ' + (e?.error || e?.message)); }
  };

  const openDetail = (r) => setDetail(r);

  return (
    <>
      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg,#7c3aed,#6366f1)', color: '#fff', padding: '16px 20px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 900, marginBottom: 2 }}>Mes Rendez-vous</h1>
            <div style={{ fontSize: 12.5, opacity: 0.9 }}>
              Bonjour, <strong>{user.name}</strong>
              {isAdvisor && teamData && <span> · Équipe <strong>{teamData.members?.length || 0}</strong> membres</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {showSettings && (
              <button onClick={onSettings} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '8px 14px', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>⚙️ Paramètres</button>
            )}
            <button onClick={onLogout} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '8px 14px', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Déconnexion</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
          <button onClick={() => setPeriod('today')} style={{ padding: '6px 12px', borderRadius: 100, cursor: 'pointer', fontSize: 11, fontWeight: 800, border: '2px solid ' + (period === 'today' ? '#fff' : 'rgba(255,255,255,0.3)'), background: period === 'today' ? '#fff' : 'transparent', color: period === 'today' ? '#7c3aed' : '#fff' }}>Aujourd'hui</button>
          <button onClick={() => setPeriod('week')} style={{ padding: '6px 12px', borderRadius: 100, cursor: 'pointer', fontSize: 11, fontWeight: 800, border: '2px solid ' + (period === 'week' ? '#fff' : 'rgba(255,255,255,0.3)'), background: period === 'week' ? '#fff' : 'transparent', color: period === 'week' ? '#7c3aed' : '#fff' }}>Cette semaine</button>
          <button onClick={() => setPeriod('upcoming')} style={{ padding: '6px 12px', borderRadius: 100, cursor: 'pointer', fontSize: 11, fontWeight: 800, border: '2px solid ' + (period === 'upcoming' ? '#fff' : 'rgba(255,255,255,0.3)'), background: period === 'upcoming' ? '#fff' : 'transparent', color: period === 'upcoming' ? '#7c3aed' : '#fff' }}>À venir</button>
          <select value={statut} onChange={e => setStatut(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, border: '2px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff' }}>
            <option value="" style={{ color: '#222' }}>Tous statuts</option>
            {Object.entries(STATUTS).map(([k, m]) => <option key={k} value={k} style={{ color: '#222' }}>{m.label}</option>)}
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher nom / tel…" style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, border: '2px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff', width: 180 }} />
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#10b981', color: '#fff', fontWeight: 800, fontSize: 13, borderRadius: 999, padding: '10px 20px', boxShadow: '0 4px 16px rgba(16,185,129,0.4)', zIndex: 1000 }}>
          {toast}
        </div>
      )}

      {/* Main Content */}
      <main style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
        {err && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 8, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
            {err} <button onClick={load} style={{ marginLeft: 12, background: 'none', border: 'none', color: '#b91c1c', fontWeight: 800, textDecoration: 'underline', cursor: 'pointer' }}>Réessayer</button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Chargement…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e1b4b', marginBottom: 8 }}>
              {period === 'today' ? "Aucun RDV pour aujourd'hui." : period === 'week' ? "Aucun RDV cette semaine." : "Aucun RDV à venir."}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: '#f8f7fc', textAlign: 'left' }}>
                    {['Client', 'Téléphone', 'RDV', 'Heure', 'Mode', 'Statut', 'Commentaires / Suivi', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', fontSize: 11, fontWeight: 900, textTransform: 'uppercase', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const due = r.followup_at && new Date(r.followup_at) <= new Date() && (r.statut === 'injoignable' || r.statut === 'rdv2');
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f0f5', background: r.statut === 'annule' ? 'rgba(239,68,68,0.04)' : due ? 'rgba(249,115,22,0.07)' : '#fff' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 700 }}>{[r.client_first, r.client_last].filter(Boolean).join(' ') || '—'}</td>
                        <td style={{ padding: '12px 16px', fontFamily: 'monospace' }}>{r.phone || '—'}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 700 }}>{fmtDate(r.rdv_date)}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#7c3aed' }}>{fmtTime(r.rdv_time)}</td>
                        <td style={{ padding: '12px 16px', fontSize: 12 }}>{r.mode === 'virtuel' ? '💻 Virtuel' : r.mode === 'telephonique' ? '📞 Téléphonique' : '—'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          {badge(r.statut)}
                          {due && <div style={{ fontSize: 10, color: '#f97316', fontWeight: 800, marginTop: 4 }}>⏰ À rappeler : {new Date(r.followup_at).toLocaleString('fr-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12.5, color: '#475569', maxWidth: 280 }}>
                          {r.comments && <div>{r.comments}</div>}
                          {r.retour && <div style={{ color: '#0369a1' }}>↩︎ {r.retour}</div>}
                          {r.followup_at && (r.statut === 'injoignable' || r.statut === 'rdv2') && (
                            <div style={{ color: '#f97316', fontWeight: 700, marginTop: 4 }}>
                              {r.statut === 'injoignable' ? 'Vous rappelez' : 'Client rappelle'} : {new Date(r.followup_at).toLocaleString('fr-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => handleRetour(r, 'rencontre')} style={{ marginRight: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #93c5fd', background: '#eff6ff', color: '#2563eb', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✅ Rencontre</button>
                          <button onClick={() => handleRetour(r, 'no_show')} style={{ marginRight: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #fcd34d', background: '#fffbeb', color: '#d97706', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>⚠️ No show</button>
                          <button onClick={() => handleRetour(r, 'injoignable')} style={{ marginRight: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #fdba74', background: '#fff7ed', color: '#ea580c', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>📞 Injoignable</button>
                          <button onClick={() => handleRetour(r, 'rdv2')} style={{ marginRight: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #d8b4fe', background: '#faf5ff', color: '#9333ea', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🔄 2ᵉ RDV</button>
                          <button onClick={() => handleRetour(r, 'refus')} style={{ marginRight: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #94a3b8', background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>❌ Refus</button>
                          <button onClick={() => openDetail(r)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #c4b5fd', background: '#f5f3ff', color: '#7c3aed', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>📝 Détail</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {detail && (
        <AppointmentDetail
          appointment={detail}
          onClose={() => setDetail(null)}
          onUpdate={load}
        />
      )}
    </>
  );
}